/**
 * Backend to UI Data Mapper
 * Converts backend domain models to UI models for the workflow designer
 */

import { ProcessElement, Connection } from '@flow-studio/api';
import { WorkflowNode, WorkflowConnection } from '@flow-studio/types';

/**
 * Map ProcessElement (backend) to WorkflowNode (UI)
 */
export const mapProcessElementToNode = (element: ProcessElement): WorkflowNode => {
  return {
    id: element.ProcessElementKey, // Use ProcessElementKey as the node ID for React Flow
    type: getNodeTypeFromBackend(element.ProcessElementTypeID),
    position: {
      x: element.PositionX || 0,
      y: element.PositionY || 0,
    },
    processElementId: element.ProcessElementID, // Root-level reference - matches API's ProcessElementID
    selected: element.IsSelected,         // Root-level React Flow selection state
    data: ({
      // Identity
      nodeKey: element.ProcessElementKey,

      // Display
      displayName: element.DisplayName || element.Name,
      description: element.Description || '',
      category: getCategoryFromTypeId(element.ProcessElementTypeID),
      iconUrl: element.IconUrl || '',
      color: element.Color,

      // Execution settings
      isDisabled: element.IsDisabled,
      isTrigger: element.IsTrigger,
      continueOnFail: element.ContinueOnFail,
      alwaysOutputData: element.AlwaysOutputData,
      retryOnFail: element.RetryOnFail,
      maxRetries: element.MaxRetries,
      timeout: element.Timeout || 30,

      // Parameters loaded separately via dedicated endpoint
      parameters: {},

      // AI Extension
      connectorId: element.ConnectorID,

      // UI State
      collapsed: false,

      // Input/Output ports will be loaded separately
      inputPorts: [],
      outputPorts: [],
    }) as any,
  };
};

/**
 * Map Connection (backend) to WorkflowConnection (UI)
 */
export const mapConnectionToEdge = (connection: Connection): WorkflowConnection => {
  return {
    id: `connection-${connection.ConnectionID}`,
    source: `element-${connection.SourceProcessElementID}`, // Will be mapped to ProcessElementKey
    target: `element-${connection.TargetProcessElementID}`, // Will be mapped to ProcessElementKey
    sourceHandle: connection.SourcePortKey || 'main',
    targetHandle: connection.TargetPortKey || 'main',
    type: getEdgeTypeFromBackend(connection.EdgeTypeID),
    animated: connection.IsConditional,
    label: connection.Label,
    data: {
      connectionId: connection.ConnectionID,
      connectionTypeId: connection.ConnectionTypeID,
      isConditional: connection.IsConditional,
      condition: connection.Condition,
      edgeStyleId: connection.EdgeStyleID,
    },
  };
};

/**
 * Map multiple ProcessElements to WorkflowNodes
 */
export const mapProcessElementsToNodes = (elements: ProcessElement[]): WorkflowNode[] => {
  return elements.map(mapProcessElementToNode);
};

/**
 * Map multiple Connections to WorkflowConnections
 */
export const mapConnectionsToEdges = (connections: Connection[]): WorkflowConnection[] => {
  // First, create a map of ProcessElementID to ProcessElementKey
  // This is needed because React Flow uses node IDs (ProcessElementKey)
  // but connections reference ProcessElementIDs

  return connections.map(connection => {
    const edge = mapConnectionToEdge(connection);
    // Note: Source and target will need to be replaced with actual ProcessElementKeys
    // This should be done in the orchestration service
    return edge;
  });
};

/**
 * Helper: Get node type from backend ProcessElementTypeID
 * TODO: Load from ProcessElementTypes table
 */
const getNodeTypeFromBackend = (typeId: number): string => {
  // Map ProcessElementTypeID to React Flow node types
  // This is a temporary mapping - should be loaded from backend
  const typeMap: Record<number, string> = {
    1: 'customNode', // HTTP Request
    2: 'customNode', // AI Agent
    3: 'customNode', // Transform
    4: 'customNode', // Database
    5: 'diamondNode', // Conditional
    6: 'customNode', // Trigger
  };
  return typeMap[typeId] || 'customNode';
};

/**
 * Helper: Get category from ProcessElementTypeID
 * TODO: Load from ProcessElementTypes table
 */
const getCategoryFromTypeId = (typeId: number): string => {
  const categoryMap: Record<number, string> = {
    1: 'http',
    2: 'ai',
    3: 'transform',
    4: 'database',
    5: 'logic',
    6: 'trigger',
  };
  return categoryMap[typeId] || 'custom';
};

/**
 * Helper: Get edge type from backend EdgeTypeID
 */
type ReactFlowEdgeType = 'default' | 'straight' | 'step' | 'smoothstep' | 'simplebezier';

const getEdgeTypeFromBackend = (edgeTypeId?: number): ReactFlowEdgeType => {
  if (!edgeTypeId) return 'smoothstep';

  const edgeTypeMap: Record<number, ReactFlowEdgeType> = {
    1: 'smoothstep',
    2: 'straight',
    3: 'step',
    4: 'default', // bezier → default (React Flow's standard bezier type)
  };
  return edgeTypeMap[edgeTypeId] || 'smoothstep';
};
